/**
 * PROJECT DETAIL VIEW
 *
 * Shared content for project detail — used by both ProjectDetailModal (Dialog)
 * and ProjectsPage (standalone page).  Everything lives here so that the
 * two surfaces stay in sync without duplicating code.
 */

import { useState, useEffect, lazy, Suspense } from "react";
import { useWorldStore, type ProjectShape, type ProjectStatus } from "@/stores/worldStore";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useSidebarStore, type ProjectSubTab } from "@/stores/sidebarStore";
import { ColonyBadge } from "@/components/colony/ColonyBadge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Kanban, ExternalLink,
  Users, Save, Play, Square, Circle,
  Hexagon, Database, Cog, Factory, RectangleHorizontal, Server,
  Trash2, Loader2, RefreshCw
} from "lucide-react";
import { useSelectionStore } from "@/stores/selectionStore";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { GitTab } from "@/features/factory/git";
import { KanbanBoard } from "@/features/factory/kanban/KanbanBoard";
import { TerminalView } from "@/features/factory/terminal";
import { CloudTab } from "@/features/factory/cloud";
import { useToast } from "@/hooks/use-toast";
import { useWorkspaceFeatures } from "@/hooks/useWorkspaceFeatures";
import { ColonyChatTab } from "@/features/factory/ai-chat/ColonyChatTab";
import { ColonyTerminalShell } from "@/features/factory/terminal/ColonyTerminalShell";
import { DocumentsTab } from "@/features/documents/DocumentsTab";

// Lazy-load LocalTerminalView so Tauri code is not bundled in web builds
const LocalTerminalView = lazy(() =>
  import("@/features/factory/terminal/LocalTerminalView").then((m) => ({
    default: m.LocalTerminalView,
  }))
);

// Lazy-load LocalGitTab for local workspace git
const LocalGitTab = lazy(() =>
  import("@/features/factory/git/local").then((m) => ({
    default: m.LocalGitTab,
  }))
);

// Lazy-load SecretsTab so keychain code is not bundled in web builds
const SecretsTab = lazy(() =>
  import("@/features/factory/secrets/SecretsTab").then((m) => ({
    default: m.SecretsTab,
  }))
);

// ── Constants ────────────────────────────────────────────────────────

const statusColors: Record<ProjectStatus, string> = {
  healthy: "bg-green-500/10 text-green-600 border-green-500/20",
  degraded: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  failing: "bg-red-500/10 text-red-600 border-red-500/20",
};

const shapeOptions: { value: ProjectShape; label: string; icon: typeof Square }[] = [
  { value: "square", label: "Square", icon: Square },
  { value: "circle", label: "Circle", icon: Circle },
  { value: "rectangle", label: "Rectangle", icon: RectangleHorizontal },
  { value: "hexagon", label: "Hexagon", icon: Hexagon },
  { value: "gear", label: "Gear", icon: Cog },
  { value: "database", label: "Database", icon: Database },
  { value: "server", label: "Server", icon: Server },
  { value: "factory", label: "Factory", icon: Factory },
];

// ── Last-visited project persistence ─────────────────────────────────

function getLastProjectKey(workspaceId: string) {
  return `factory-last-project-${workspaceId}`;
}

export function getLastVisitedProject(workspaceId: string): number | null {
  try {
    const raw = localStorage.getItem(getLastProjectKey(workspaceId));
    return raw ? parseInt(raw, 10) : null;
  } catch { return null; }
}

export function setLastVisitedProject(workspaceId: string, projectId: number) {
  try { localStorage.setItem(getLastProjectKey(workspaceId), String(projectId)); } catch {}
}

// ── Active tab persistence (global, survives workspace switches) ─────
const ACTIVE_TAB_KEY = "factory-active-tab";

function getPersistedActiveTab(): string | null {
  try { return localStorage.getItem(ACTIVE_TAB_KEY); } catch { return null; }
}

function persistActiveTab(tab: string) {
  try { localStorage.setItem(ACTIVE_TAB_KEY, tab); } catch {}
}

// ── Main Component ───────────────────────────────────────────────────

export interface ProjectDetailViewProps {
  /** Initial project to show */
  projectId: number;
  /** Called when the user clicks the close / X button */
  onClose?: () => void;
  /** Initial tab to open */
  initialTab?: string;
  /**
   * When true, add left padding on the project tab strip so it doesn't
   * overlap the macOS traffic-light buttons.  Set to true when the view
   * is rendered inside a full-screen dialog (no TopNavbar above).
   */
  showMacPadding?: boolean;
  /** Extra className on the root element */
  className?: string;
}

export function ProjectDetailView({
  projectId,
  onClose,
  initialTab,
  showMacPadding = false,
  className,
}: ProjectDetailViewProps) {
  const { projects, agents, tasks, updateProject, removeProject, syncProjectIssues, isSaving } = useWorldStore();
  const { clearSelection } = useSelectionStore();
  const workspaceBackendId = useWorkspaceStore((s) => s.getActiveWorkspaceBackendId());

  // Internal project ID for switching without closing
  const [currentProjectId, setCurrentProjectId] = useState(projectId);
  useEffect(() => {
    setCurrentProjectId(projectId);
  }, [projectId]);

  const project = projects.find((p) => p.id === currentProjectId);
  const { selectedProjectSubTab, setSelectedProjectSubTab } = useSidebarStore();

  // Map sidebar tab names to internal tab names (board → kanban)
  const sidebarToInternal = (tab: ProjectSubTab): string => tab === "board" ? "kanban" : tab;
  const internalToSidebar = (tab: string): ProjectSubTab => tab === "kanban" ? "board" : tab as ProjectSubTab;

  const activeTab = initialTab ? sidebarToInternal(initialTab as ProjectSubTab) : sidebarToInternal(selectedProjectSubTab);
  const setActiveTab = (tab: string) => {
    setSelectedProjectSubTab(internalToSidebar(tab));
    persistActiveTab(tab);
  };
  const [editedName, setEditedName] = useState(project?.name || "");
  const [editedRepo, setEditedRepo] = useState(project?.githubRepo || "");
  const [editedDescription, setEditedDescription] = useState(project?.description || "");
  const [editedSubdirectory, setEditedSubdirectory] = useState(project?.subdirectory || "");
  const [editedDevCommand, setEditedDevCommand] = useState(project?.projectConfig?.devCommand || "");
  const [editedPort, setEditedPort] = useState(project?.projectConfig?.port?.toString() || "");
  const [editedDockerfilePath, setEditedDockerfilePath] = useState(project?.projectConfig?.dockerfilePath || "");
  const [editedBuildContext, setEditedBuildContext] = useState(project?.projectConfig?.buildContext || "");
  const [editedClaudeMdPath, setEditedClaudeMdPath] = useState(project?.projectConfig?.claudeMdPath || "");
  const { toast } = useToast();
  const { canSyncIssues, canTerminal, canCloudResources, canSecrets, isLocalWorkspace, isCloudWorkspace } = useWorkspaceFeatures();

  // Guard: if current tab isn't available in this workspace, fall back
  useEffect(() => {
    if (activeTab === "terminal" && !canTerminal) setSelectedProjectSubTab("overview");
    if (activeTab === "cloud" && !canCloudResources) setSelectedProjectSubTab("overview");
    if (activeTab === "secrets" && !canSecrets) setSelectedProjectSubTab("overview");
  }, [canTerminal, canCloudResources, canSecrets, activeTab, setSelectedProjectSubTab]);

  const activeWsId = useWorkspaceStore((s) => s.activeWorkspaceId);

  // Keep form fields in sync with latest project data
  useEffect(() => {
    if (project) {
      setEditedName(project.name);
      setEditedRepo(project.githubRepo || "");
      setEditedDescription(project.description || "");
      setEditedSubdirectory(project.subdirectory || "");
      setEditedDevCommand(project.projectConfig?.devCommand || "");
      setEditedPort(project.projectConfig?.port?.toString() || "");
      setEditedDockerfilePath(project.projectConfig?.dockerfilePath || "");
      setEditedBuildContext(project.projectConfig?.buildContext || "");
      setEditedClaudeMdPath(project.projectConfig?.claudeMdPath || "");
    }
  }, [currentProjectId, project?.name, project?.githubRepo, project?.description, project?.subdirectory, project?.projectConfig]);

  // Track last visited project
  useEffect(() => {
    if (activeWsId && currentProjectId) {
      setLastVisitedProject(activeWsId, currentProjectId);
    }
  }, [activeWsId, currentProjectId]);

  // Sync sidebar tab when initialTab prop changes
  useEffect(() => {
    if (initialTab) {
      setSelectedProjectSubTab(internalToSidebar(initialTab));
    }
  }, [initialTab, setSelectedProjectSubTab]);

  if (!project) return null;

  const projectAgents = agents.filter((a) => a.projectId === project.id);
  const projectTasks = tasks.filter((t) => t.projectId === project.id);

  const normalizeSubdirectory = (value: string) =>
    value.trim().replace(/^[/\\]+/, "").replace(/[/\\]+$/, "");

  const handleSaveSettings = async () => {
    const configUpdate: Record<string, unknown> = {};
    if (editedDevCommand) configUpdate.devCommand = editedDevCommand;
    if (editedPort) configUpdate.port = parseInt(editedPort) || undefined;
    if (editedDockerfilePath) configUpdate.dockerfilePath = editedDockerfilePath;
    if (editedBuildContext) configUpdate.buildContext = editedBuildContext;
    if (editedClaudeMdPath) configUpdate.claudeMdPath = editedClaudeMdPath;
    const normalizedSubdirectory = normalizeSubdirectory(editedSubdirectory);

    const ok = await updateProject(
      project.id,
      {
        name: editedName,
        githubRepo: editedRepo.trim() || undefined,
        description: editedDescription,
        subdirectory: normalizedSubdirectory || undefined,
        projectConfig: Object.keys(configUpdate).length > 0 ? configUpdate : undefined,
      },
      { immediate: true }
    );

    if (ok) {
      toast({
        title: "Saved",
        description: "Project settings updated.",
      });
    }
  };

  const handleSyncIssues = async () => {
    try {
      const result = await syncProjectIssues(project.id);
      if (result) {
        toast({
          title: "GitHub issues synced",
          description: `${result.created} new, ${result.updated} updated (${result.synced} total).`,
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Sync failed",
        description: error instanceof Error ? error.message : "Unknown error while syncing issues.",
      });
    }
  };

  const handleRepoChange = async (repo: string | null, installationId: number | null) => {
    const success = await updateProject(
      project.id,
      { githubRepo: repo || undefined },
      { immediate: true }
    );

    if (repo) {
      setEditedRepo(repo);
    }

    if (!success) {
      toast({
        variant: "destructive",
        title: "Save failed",
        description: "Could not save the GitHub repository.",
      });
      return;
    }

    if (repo && canSyncIssues) {
      try {
        const result = await syncProjectIssues(project.id);
        if (result) {
          toast({
            title: "GitHub issues synced",
            description: `${result.created} new, ${result.updated} updated (${result.synced} total).`,
          });
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Sync failed",
          description: error instanceof Error ? error.message : "Unknown error while syncing issues.",
        });
      }
    }
  };

  const handleRemoveProject = async () => {
    const success = await removeProject(project.id);
    if (success) {
      clearSelection();
      onClose?.();
    }
  };

  const currentShape = shapeOptions.find((s) => s.value === project.shape) || shapeOptions[0];
  const ShapeIcon = currentShape.icon;
  const localTerminalScopeKey = `local:${activeWsId || "unknown"}:project:${project.id}`;

  return (
    <div className={`flex flex-col overflow-hidden ${className || ""}`}>
      {/* ─── Colony Breadcrumb Header ─── */}
      <div className="flex items-center h-11 px-5 border-b flex-shrink-0" style={{ borderColor: "var(--colony-border)", background: "var(--colony-surface)" }}>
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="text-[14px] font-semibold text-colony-text-primary truncate" data-testid="text-modal-project-name">
            {project.name}
          </h2>
          <span className="text-colony-text-tertiary text-[13px]">/</span>
          <span className="text-[13px] text-colony-text-secondary capitalize">
            {activeTab === "kanban" ? "Board" : activeTab.replace("_", " ")}
          </span>
          <ColonyBadge
            status={project.status === "healthy" ? "green" : project.status === "degraded" ? "amber" : "red"}
            dot
          >
            {project.status}
          </ColonyBadge>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <Button
            size="sm"
            variant="secondary"
            className="gap-2"
            onClick={handleSaveSettings}
            data-testid="button-header-save"
            disabled={isSaving}
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isSaving ? "Saving\u2026" : "Save"}
          </Button>
          {canSyncIssues && (
            <Button
              size="sm"
              variant="outline"
              className="gap-2"
              onClick={handleSyncIssues}
              data-testid="button-header-sync-issues"
            >
              <RefreshCw className="w-4 h-4" />
              Sync issues
            </Button>
          )}
        </div>
      </div>

      {/* ─── Tab Content (conditional rendering based on sidebar) ─── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Overview */}
        {activeTab === "overview" && (
          <div className="h-full overflow-y-auto">
            <div className="max-w-[1200px] mx-auto px-6 py-6">
              <div className="grid grid-cols-[1fr_1fr] gap-6 items-start">
                {/* ─── Left Column: Project Settings ─── */}
                <div
                  className="rounded-[8px] border border-[rgba(255,255,255,0.06)] overflow-hidden"
                  style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-subtle)" }}
                >
                  <div className="px-5 py-4 border-b border-[rgba(255,255,255,0.06)]">
                    <span className="text-[14px] font-semibold text-colony-text-heading tracking-[-0.015em]">
                      Project Settings
                    </span>
                  </div>
                  <div className="px-5 py-6 space-y-6">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[13px] font-medium text-colony-text-primary">Project Name</label>
                      <div className="relative flex items-center h-9 rounded-[6px] bg-[#151518] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.10)] focus-within:border-[rgba(0,178,200,0.4)] focus-within:shadow-[0_0_0_3px_rgba(0,178,200,0.12)] transition-all duration-150" style={{ boxShadow: "var(--shadow-inset)" }}>
                        <input
                          type="text"
                          value={editedName}
                          onChange={(e) => setEditedName(e.target.value)}
                          className="flex-1 h-full bg-transparent px-3 text-[13px] text-colony-text-primary placeholder:text-colony-text-tertiary outline-none"
                          data-testid="input-modal-project-name"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <label className="text-[13px] font-medium text-colony-text-primary">GitHub Repository</label>
                      <div className="flex gap-2">
                        <div className="relative flex items-center h-9 rounded-[6px] bg-[#151518] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.10)] focus-within:border-[rgba(0,178,200,0.4)] focus-within:shadow-[0_0_0_3px_rgba(0,178,200,0.12)] transition-all duration-150 flex-1" style={{ boxShadow: "var(--shadow-inset)" }}>
                          <input
                            type="text"
                            value={editedRepo}
                            onChange={(e) => setEditedRepo(e.target.value)}
                            placeholder="owner/repository"
                            className="flex-1 h-full bg-transparent px-3 text-[13px] text-colony-text-primary placeholder:text-colony-text-tertiary outline-none"
                            data-testid="input-github-repo"
                          />
                          {editedRepo && (
                            <a href={`https://github.com/${editedRepo}`} target="_blank" rel="noopener noreferrer" className="flex items-center pr-2.5 text-colony-text-tertiary cursor-pointer hover:text-colony-text-secondary transition-colors">
                              <ExternalLink size={13} strokeWidth={1.5} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <label className="text-[13px] font-medium text-colony-text-primary">Description</label>
                      <textarea
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        placeholder="Project description..."
                        rows={3}
                        className="min-h-[80px] rounded-[6px] px-3 py-2.5 text-[13px] bg-[#151518] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.10)] focus:border-[rgba(0,178,200,0.4)] focus:shadow-[0_0_0_3px_rgba(0,178,200,0.12)] text-colony-text-primary placeholder:text-colony-text-tertiary outline-none resize-y transition-all duration-150 leading-[1.65]"
                        data-testid="input-project-description"
                      />
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <label className="text-[13px] font-medium text-colony-text-primary">Status</label>
                      <Select
                        value={project.status}
                        onValueChange={(v) => updateProject(project.id, { status: v as ProjectStatus })}
                      >
                        <SelectTrigger className="h-9 rounded-[6px] bg-[#151518] border-[rgba(255,255,255,0.08)] text-[13px] text-colony-text-primary" data-testid="select-modal-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="healthy">Healthy</SelectItem>
                          <SelectItem value="degraded">Degraded</SelectItem>
                          <SelectItem value="failing">Failing</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <label className="text-[13px] font-medium text-colony-text-primary">Shape</label>
                      <Select
                        value={project.shape || "square"}
                        onValueChange={(v) => updateProject(project.id, { shape: v as ProjectShape })}
                      >
                        <SelectTrigger className="h-9 rounded-[6px] bg-[#151518] border-[rgba(255,255,255,0.08)] text-[13px] text-colony-text-primary" data-testid="select-modal-shape">
                          <SelectValue>
                            <div className="flex items-center gap-2">
                              <ShapeIcon className="w-4 h-4" />
                              <span>{currentShape.label}</span>
                            </div>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {shapeOptions.map((shape) => {
                            const Icon = shape.icon;
                            return (
                              <SelectItem key={shape.value} value={shape.value}>
                                <div className="flex items-center gap-2">
                                  <Icon className="w-4 h-4" />
                                  <span>{shape.label}</span>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <label className="text-[13px] font-medium text-colony-text-primary">Subdirectory (optional)</label>
                      <div className="relative flex items-center h-9 rounded-[6px] bg-[#151518] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.10)] focus-within:border-[rgba(0,178,200,0.4)] focus-within:shadow-[0_0_0_3px_rgba(0,178,200,0.12)] transition-all duration-150" style={{ boxShadow: "var(--shadow-inset)" }}>
                        <input
                          type="text"
                          value={editedSubdirectory}
                          onChange={(e) => setEditedSubdirectory(e.target.value)}
                          placeholder="packages/api"
                          className="flex-1 h-full bg-transparent px-3 text-[13px] text-colony-text-primary placeholder:text-colony-text-tertiary outline-none"
                          data-testid="input-subdirectory"
                        />
                      </div>
                      <span className="text-[11px] text-colony-text-tertiary">Relative path within the repository</span>
                    </div>

                    {project.monorepoGroup && (
                      <div className="text-[11px] text-colony-text-tertiary px-1">
                        Monorepo group: <span className="font-mono">{project.monorepoGroup}</span>
                      </div>
                    )}

                    <button
                      onClick={handleSaveSettings}
                      className="w-full flex items-center justify-center gap-2 h-9 rounded-[6px] bg-colony-brand hover:bg-colony-brand-hover hover:-translate-y-px active:scale-[0.98] text-white text-[13px] font-medium transition-all duration-150 cursor-pointer"
                      style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12), 0 1px 2px rgba(0,0,0,0.2)" }}
                      data-testid="button-save-settings"
                    >
                      <Save className="w-4 h-4" />
                      Save Changes
                    </button>
                  </div>
                </div>

                {/* ─── Right Column ─── */}
                <div className="space-y-6">
                  {/* Project Config */}
                  <div
                    className="rounded-[8px] border border-[rgba(255,255,255,0.06)] overflow-hidden"
                    style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-subtle)" }}
                  >
                    <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[rgba(255,255,255,0.06)]">
                      <Cog className="w-4 h-4 text-[#6E6E7A]" />
                      <span className="text-[14px] font-semibold text-colony-text-heading tracking-[-0.015em]">
                        Project Config
                      </span>
                    </div>
                    <div className="px-5 py-5 space-y-4">
                      {[
                        { label: "Dev Command", value: editedDevCommand, setter: setEditedDevCommand, placeholder: "npm run dev", testId: "input-dev-command" },
                        { label: "Port", value: editedPort, setter: setEditedPort, placeholder: "3000", testId: "input-port", type: "number" },
                        { label: "Dockerfile Path", value: editedDockerfilePath, setter: setEditedDockerfilePath, placeholder: "Dockerfile", testId: "input-dockerfile-path" },
                        { label: "Build Context", value: editedBuildContext, setter: setEditedBuildContext, placeholder: ".", testId: "input-build-context" },
                        { label: "CLAUDE.md Path", value: editedClaudeMdPath, setter: setEditedClaudeMdPath, placeholder: "CLAUDE.md", testId: "input-claude-md-path" },
                      ].map((field) => (
                        <div key={field.testId} className="flex flex-col gap-1">
                          <label className="text-[12px] font-medium text-colony-text-secondary">{field.label}</label>
                          <div className="relative flex items-center h-8 rounded-[6px] bg-[#151518] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.10)] focus-within:border-[rgba(0,178,200,0.4)] focus-within:shadow-[0_0_0_3px_rgba(0,178,200,0.12)] transition-all duration-150">
                            <input
                              type={field.type || "text"}
                              value={field.value}
                              onChange={(e) => field.setter(e.target.value)}
                              placeholder={field.placeholder}
                              className="flex-1 h-full bg-transparent px-2.5 text-[13px] text-colony-text-primary placeholder:text-colony-text-tertiary outline-none"
                              data-testid={field.testId}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Agents */}
                  <div
                    className="rounded-[8px] border border-[rgba(255,255,255,0.06)] overflow-hidden"
                    style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-subtle)" }}
                  >
                    <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[rgba(255,255,255,0.06)]">
                      <Users className="w-4 h-4 text-[#6E6E7A]" />
                      <span className="text-[14px] font-semibold text-colony-text-heading tracking-[-0.015em]">
                        Agents ({projectAgents.length})
                      </span>
                    </div>
                    <div className="px-5 py-5">
                      <ScrollArea className="max-h-40">
                        {projectAgents.map((agent) => (
                          <div key={agent.id} className="flex items-center gap-3 px-0 py-2.5 border-b border-colony-border last:border-0 hover:bg-colony-surface-hover transition-colors">
                            <div className={`w-2 h-2 rounded-full ${agent.state === "busy" ? "bg-[var(--colony-blue)]" : agent.state === "queued" ? "bg-[var(--colony-violet)]" : "bg-[var(--colony-text-tertiary)]"}`} />
                            <span className="flex-1 text-[13px] text-colony-text-primary">{agent.name}</span>
                            <span className="text-[11px] font-medium text-colony-text-tertiary bg-colony-surface-elevated px-2 py-0.5 rounded-[3px] capitalize">{agent.state}</span>
                          </div>
                        ))}
                        {projectAgents.length === 0 && (
                          <div className="flex items-center justify-center py-6">
                            <span className="text-[13px] text-colony-text-tertiary">No agents assigned</span>
                          </div>
                        )}
                      </ScrollArea>
                    </div>
                  </div>

                  {/* Tasks */}
                  <div
                    className="rounded-[8px] border border-[rgba(255,255,255,0.06)] overflow-hidden"
                    style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-subtle)" }}
                  >
                    <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[rgba(255,255,255,0.06)]">
                      <Kanban className="w-4 h-4 text-[#6E6E7A]" />
                      <span className="text-[14px] font-semibold text-colony-text-heading tracking-[-0.015em]">
                        Tasks ({projectTasks.length})
                      </span>
                    </div>
                    <div className="px-1 py-1">
                      <ScrollArea className="max-h-40">
                        {projectTasks.map((task, idx) => (
                          <div
                            key={task.id}
                            className={`flex items-center gap-3 px-4 py-3 ${
                              idx < projectTasks.length - 1 ? "border-b border-colony-border" : ""
                            } hover:bg-colony-surface-hover transition-colors cursor-pointer`}
                          >
                            <span className="text-[12px] text-colony-text-tertiary">•</span>
                            <span className="text-[13px] text-colony-text-primary flex-1 truncate">{task.title}</span>
                            <span className="text-[11px] font-medium text-colony-text-tertiary bg-colony-surface-elevated px-2 py-0.5 rounded-[3px] capitalize">{task.pipelineStage}</span>
                          </div>
                        ))}
                        {projectTasks.length === 0 && (
                          <div className="flex items-center justify-center py-6">
                            <span className="text-[13px] text-colony-text-tertiary">No active tasks</span>
                          </div>
                        )}
                      </ScrollArea>
                    </div>
                  </div>
                </div>
              </div>

              {/* ─── Danger Zone ─── */}
              <div className="mt-8 rounded-[8px] border border-colony-red/20 bg-[rgba(239,68,68,0.03)] overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4">
                  <div>
                    <span className="text-[14px] font-semibold text-colony-red">Danger Zone</span>
                    <p className="text-[12px] text-colony-text-tertiary mt-0.5">Irreversible actions for this project</p>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="flex items-center gap-1.5 h-8 px-3 rounded-[6px] bg-colony-red/10 border border-colony-red/20 text-colony-red text-[12px] font-medium hover:bg-colony-red/20 transition-all duration-150 cursor-pointer">
                        <Trash2 className="w-3.5 h-3.5" />
                        Remove Project
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This action cannot be undone. This will permanently delete the <strong>{project.name}</strong> project, unassign all its agents, and remove all associated tasks and data.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleRemoveProject}>Yes, remove project</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Kanban / Board */}
        {activeTab === "kanban" && (
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            <KanbanBoard projectId={project.id} />
          </div>
        )}

        {/* Terminal */}
        {activeTab === "terminal" && (
          <ColonyTerminalShell projectName={project.name}>
            {isLocalWorkspace && project.localPath ? (
              <Suspense fallback={<div className="flex-1 flex items-center justify-center text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading terminal...</div>}>
                <LocalTerminalView cwd={project.localPath} className="flex-1" scopeKey={localTerminalScopeKey} />
              </Suspense>
            ) : (
              <TerminalView projectId={project.id} className="flex-1" />
            )}
          </ColonyTerminalShell>
        )}

        {/* Chat */}
        {activeTab === "chat" && (
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <ColonyChatTab
              projectId={project.id}
              isLocal={!!isLocalWorkspace && !!project.localPath}
              localPath={project.localPath}
            />
          </div>
        )}

        {/* Git */}
        {activeTab === "git" && (
          <div className="mx-6 mt-4 flex-1 overflow-auto">
            {isLocalWorkspace && project.localPath ? (
              <Suspense fallback={<div className="flex items-center justify-center py-8 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading Git...</div>}>
                <LocalGitTab projectId={project.id} cwd={project.localPath} githubRepo={project.githubRepo} workspaceKey={activeWsId} backendWorkspaceId={workspaceBackendId} />
              </Suspense>
            ) : (
              <GitTab projectId={project.id} githubRepo={project.githubRepo || null} installationId={null} workspaceId={workspaceBackendId} onRepoChange={(repo, installationId) => { void handleRepoChange(repo, installationId); }} />
            )}
          </div>
        )}

        {/* Documents */}
        {activeTab === "documents" && (
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            <DocumentsTab project={project} />
          </div>
        )}

        {/* Secrets */}
        {activeTab === "secrets" && canSecrets && (
          <div className="mx-6 mt-4 flex-1 overflow-auto">
            <Suspense fallback={<div className="flex items-center justify-center py-8 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading Secrets...</div>}>
              <SecretsTab projectId={project.id} workspaceKey={activeWsId} backendWorkspaceId={workspaceBackendId} />
            </Suspense>
          </div>
        )}

        {/* Cloud */}
        {activeTab === "cloud" && canCloudResources && (
          <div className="mx-6 mt-4 flex-1 overflow-auto">
            <CloudTab projectId={project.id} projectName={project.name} />
          </div>
        )}
      </div>
    </div>
  );
}
